import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import axios from "axios";
import { Briefcase, CheckCircle, Mail, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConfirmationPage() {
  const { submissionId } = useParams();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchSubmission = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/submission/${submissionId}`);
      setSubmission(data);
    } catch (e) {
      // silently handle - UI shows "not found" state
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-600 mb-4">Submission not found</p>
          <Link to="/">
            <Button className="bg-[#002FA7] text-white hover:bg-[#002280] rounded-sm">
              Go Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[#002FA7]" />
            <span className="font-black text-xl tracking-tight text-zinc-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              GetJobInterviews
            </span>
          </Link>
        </div>
      </nav>

      {/* Confirmation Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 
            className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            data-testid="confirmation-title"
          >
            We're On It, {submission.full_name}!
          </h1>
          <p className="text-lg text-zinc-600">
            Your job applications are being prepared right now.
          </p>
          <p className="text-[#002FA7] font-medium mt-2">
            Expect your first matches within 24-48 hours.
          </p>
        </div>

        {/* Order Summary */}
        <div className="bg-white border border-zinc-200 p-6 mb-6">
          <h2 className="font-semibold text-zinc-900 mb-4">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-zinc-600">Plan</span>
              <span className="font-medium text-zinc-900">
                {submission.plan === "starter" ? "Starter" : "Pro"} - {submission.job_count} applications
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Target Role</span>
              <span className="font-medium text-zinc-900">{submission.target_role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Location</span>
              <span className="font-medium text-zinc-900">{submission.location}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-600">Job Type</span>
              <span className="font-medium text-zinc-900 capitalize">{submission.job_type}</span>
            </div>
            <div className="border-t border-zinc-200 pt-3 mt-3 flex justify-between">
              <span className="text-zinc-600">Total Paid</span>
              <span className="font-bold text-zinc-900">
                ${submission.amount / 100}
              </span>
            </div>
          </div>
        </div>

        {/* What's Next */}
        <div className="bg-[#002FA7]/5 border border-[#002FA7]/20 p-6 mb-6">
          <h2 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#002FA7]" />
            What's Happening Right Now
          </h2>
          <ol className="space-y-3 text-zinc-700">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-green-500 text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">✓</span>
              <span><strong>Now:</strong> We're analyzing your profile and preferences</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#002FA7] text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">2</span>
              <span><strong>Next 24h:</strong> Finding {submission.job_count}+ relevant job openings</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#002FA7] text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">3</span>
              <span><strong>24-48h:</strong> Preparing tailored cover letters & emails</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#002FA7] text-white text-sm font-bold rounded-full flex items-center justify-center flex-shrink-0">4</span>
              <span><strong>Delivery:</strong> Complete package sent to your email</span>
            </li>
          </ol>
        </div>

        {/* Email Notification */}
        <div className="bg-white border border-zinc-200 p-6 text-center">
          <Mail className="w-8 h-8 text-[#002FA7] mx-auto mb-3" />
          <p className="text-zinc-700">
            A confirmation email has been sent to<br />
            <strong className="text-zinc-900">{submission.email}</strong>
          </p>
        </div>

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button variant="outline" className="rounded-sm">
              <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
              Back to Home
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-900 text-white mt-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 text-center">
          <p className="text-sm text-zinc-400">
            Questions? Email us at support@getjobinterviews.com
          </p>
        </div>
      </footer>
    </div>
  );
}
